/**
 * 刷新全局登录态（localStorage.user）—— 星盘站内统一刷新入口。
 *
 * 为什么需要：
 *   `localStorage.user` 不是响应式数据，各页面/组件都在 created 时各抓一次快照，
 *   所以「扫码支付开通成功后」即使写回了新 user，其它组件（顶栏、星盘页的权限与
 *   会员信息、画布的报警权益）也不会自动更新 —— 表现为"必须手动刷新页面才生效"。
 *
 * 怎么办：
 *   用登录态里已有的 id + auth_code 重新请求后端权威接口
 *   `GET /yljx/public/login?id=xxx&auth_code=xxx`，
 *   拿到整份最新 user 后通过 userContext.writeStoredTianxingUser() 写回并广播，
 *   订阅了登录态变更的页面（星盘页）即可就地重读权限、重算会员与报警权益。
 *
 * 调用时机：
 *   - 支付开通成功（付费扫码 / JSAPI 轮询成功、0 元项自助开通）；
 *   - 星盘页进入时、以及窗口重新获得焦点/回到前台时（手机扫码付款后回到电脑页）；
 *   - 任何需要"确认最新权限"的业务动作。
 *
 * 失败口径：任何失败（未登录、缺 auth_code、接口异常、返回非成功码）都静默返回 null，
 * 绝不抛错、不打断支付主流程，也不覆盖本地已有的登录态。
 *
 * 本模块顶部只依赖纯 CJS 的 userContext（可在 node 里直跑单测）；
 * 网络层通过参数注入，未注入时才延迟 require('@t/api')，避免单测解析打包别名失败。
 */
const userContext = require('../../../services/userContext')

/** 后端 /yljx/public/login 的取参口径：必须同时有 id 与 auth_code，否则视为"无法刷新" */
function buildRefreshLoginParams (user) {
  if (!user || typeof user !== 'object') {
    return null
  }
  const id = user.id === undefined || user.id === null ? '' : String(user.id).trim()
  const authCode = user.auth_code === undefined || user.auth_code === null
    ? ''
    : String(user.auth_code).trim()
  if (!id || !authCode) {
    return null
  }
  return { id, auth_code: authCode }
}

/** 登录成功码：与首页登录口径一致（0000 / 1111 都算成功），取出 user 对象 */
function resolveLoginUser (response) {
  if (!response || typeof response !== 'object') {
    return null
  }
  if (response.code !== '0000' && response.code !== '1111') {
    return null
  }
  return response.user && typeof response.user === 'object' ? response.user : null
}

/** 取默认的登录接口实现（打包环境走 @t/api；拿不到则视为不可刷新） */
function resolveDefaultApi () {
  try {
    const mod = require('../api')
    return (mod && mod.default) || mod || null
  } catch (error) {
    return null
  }
}

/**
 * 重新拉取最新用户信息并写回全局登录态。
 *
 * @param {Object} [options]
 * @param {Object} [options.user] 已知的 user（缺省时读 localStorage.user）
 * @param {Object} [options.apiReq] 登录接口实现（缺省用 @t/api，便于单测注入）
 * @returns {Promise<Object|null>} 最新 user；无需刷新 / 刷新失败返回 null
 */
async function refreshTianxingUser (options = {}) {
  const currentUser = options.user && typeof options.user === 'object'
    ? options.user
    : userContext.readStoredTianxingUser()
  const params = buildRefreshLoginParams(currentUser)
  if (!params) {
    return null
  }

  const apiReq = options.apiReq || resolveDefaultApi()
  if (!apiReq || typeof apiReq.login !== 'function') {
    return null
  }

  try {
    const response = await apiReq.login(params)
    const latestUser = resolveLoginUser(response)
    if (!latestUser) {
      return null
    }
    // 写回 + 广播（内容没变时内部会自动跳过写入与通知）
    userContext.writeStoredTianxingUser(latestUser)
    return latestUser
  } catch (error) {
    console.warn('[refreshTianxingUser] 刷新用户信息失败，保留本地登录态：', error)
    return null
  }
}

module.exports = {
  buildRefreshLoginParams,
  resolveLoginUser,
  refreshTianxingUser
}
